import {
  AdvancedSearchConfig,
  flattenAdvancedSearchConfig,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import {
  getFormFieldsPreferenceKey,
  type FormFieldsVisibilityState,
} from "@/lib/form-field-visibility";
import { readBoPreference } from "@/lib/user-preferences/store";
import { isFieldVisibleInFiche } from "./always-visible-fields";

/**
 * Advanced-search `resource` (inconsistent plural/singular, e.g. "artworks",
 * "auction") → the visibility-grid table key (formKey, e.g. "artwork").
 */
const RESOURCE_TO_TABLE: Record<string, string> = {
  artworks: "artwork",
  multipleprints: "multipleprint",
  exhibitions: "exhibition",
  bibliography: "bibliography",
  auction: "auction",
  authors: "author",
  collectors: "collector",
  audiovisuals: "audiovisual",
  documents: "document",
  writing: "writing",
  photographies: "photography",
};

/** Maps a search `resource` to the table key used by the visibility grid. */
export function resourceToTable(
  resource: string | undefined,
): string | undefined {
  return resource ? RESOURCE_TO_TABLE[resource] : undefined;
}

/** Reads the per-user field-visibility grid for a table. */
export function readTableVisibility(
  table: string,
): FormFieldsVisibilityState {
  return (
    readBoPreference<FormFieldsVisibilityState>(
      getFormFieldsPreferenceKey(table),
    ) ?? {}
  );
}

/**
 * Set of field keys currently masked (hidden) in the fiche for `table`.
 * Empty when the table is unknown (no filtering).
 */
export function getHiddenSearchFieldKeys(
  config: AdvancedSearchConfig,
  table: string | undefined,
): Set<string> {
  if (!table) return new Set();
  const visibility = readTableVisibility(table);
  const hidden = flattenAdvancedSearchConfig(config)
    .filter((field) => !isFieldVisibleInFiche(table, field.key, visibility))
    .map((field) => field.key);
  return new Set(hidden);
}

/** True when a specific field key is masked in the fiche for `table`. */
export function isSearchFieldHidden(
  table: string | undefined,
  fieldKey: string,
  visibility: FormFieldsVisibilityState,
): boolean {
  if (!table) return false;
  return !isFieldVisibleInFiche(table, fieldKey, visibility);
}
